import { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

// Highcharts modules — each adds a feature to the global Highcharts object.
// These must be imported once before any chart renders.
import "highcharts/modules/exporting";       // hamburger export menu
import "highcharts/modules/export-data";     // downloadCSV / downloadXLS
import "highcharts/modules/drilldown";       // pie drilldown
import "highcharts/modules/funnel";          // funnel series type
import "highcharts/modules/offline-exporting"; // client-side PNG/PDF without server
import "highcharts/modules/map";             // choropleth map

import mapData from "@highcharts/map-collection/countries/us/us-all.geo.json";

import type { ToggleableChart } from "./types";
import { getChartEntry } from "./registry";
import { resolveWidgetConfig } from "./widgetConfig";
import { getBarRows, getPieRows } from "./data/transformers";
import { buildContextMenu } from "./menu/contextMenu";
import ChartTableView from "./ChartTableView";
import type { WidgetDownloadOptions } from "../components/widgets/DownloadOptionsMenu";

type ChartFactoryProps = {
  chartType: string;
  chartId: string;
  data: unknown;
  spec?: unknown;
  title: string;
  // Widget description from the dashboard detail API. When present, the
  // context menu's "Download CSV" prepends Widget/Description rows.
  description?: string | null;
  fillHeight?: boolean;
  tableSourceType?: string;
  onPointClick?: (point: unknown) => void;
  // Backend-declared download capability (has_download_options/
  // download_options) — merged into this chart's existing native menu
  // (Convert to X, table view, PNG/PDF, etc.) instead of replacing it, since
  // bar/line_with_bar already have real, useful items there.
  downloadOptions?: WidgetDownloadOptions;
  onDownloadCsvData?: () => void;
  onDownloadCaseData?: () => void;
};

// ── CSV download with widget metadata ────────────────────────────────────────
// Replaces Highcharts' built-in downloadCSV: same series data (chart.getCSV),
// with Widget/Description rows prepended so the file stands on its own.

const csvCell = (val: string): string =>
  /[",\n]/.test(val) ? `"${val.replace(/"/g, '""')}"` : val;

const csvFileName = (title: string): string => {
  const slug =
    (title || "chart")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "chart";
  return `${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
};

const downloadCsvWithMeta = (
  chart: Highcharts.Chart,
  title: string,
  description: string,
) => {
  const body = (chart as any).getCSV?.() ?? "";
  const meta = [
    `${csvCell("Widget")},${csvCell(title)}`,
    `${csvCell("Description")},${csvCell(description)}`,
    "",
  ].join("\n");
  // BOM so Excel opens UTF-8 correctly
  const blob = new Blob(["\uFEFF" + meta + body], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = csvFileName(title);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// ---------------------------------------------------------------------------
// ChartFactory — the single orchestrator for every Highcharts-based chart.
//
// Responsibilities:
//   1. Registry lookup → get the renderer and config for the active chart type
//   2. Data transformation when the user converts between chart types
//      (e.g. map data → bar rows, bar data → pie rows)
//   3. Context menu injection — export items + conversion buttons
//   4. activeChartType state — tracks user-driven type changes (bar↔pie, any→table)
//   5. fillHeight charts — ResizeObserver keeps Highcharts synced with CSS height
//   6. Delegates table rendering to ChartTableView when activeChartType === "table"
// ---------------------------------------------------------------------------
export default function ChartFactory({
  chartType,
  chartId,
  data,
  spec,
  title,
  description,
  fillHeight = false,
  tableSourceType,
  onPointClick,
  downloadOptions,
  onDownloadCsvData,
  onDownloadCaseData,
}: ChartFactoryProps) {
  // The builder-saved "Set Default View", if the author pinned one — e.g. a
  // bar widget that should open as a pie. Seeded here rather than by swapping
  // the chartType prop upstream, because getBarRows(data, sourceChartType)
  // branches on the *source* type: a map/funnel/data_table swapped to "bar"
  // before it reaches here would find no rows to draw, and "Go to source
  // chart" would lead back to the wrong chart.
  //
  // Resolves to a plain string: `spec` is a fresh object on most renders, so
  // depending on it directly below would re-run the effect every pass.
  const configuredView = useMemo(
    () => resolveWidgetConfig(chartType, spec).chart_type ?? chartType,
    [chartType, spec],
  );

  // activeChartType: what is rendered right now (may differ from the prop if user converted)
  const [activeChartType, setActiveChartType] = useState(configuredView);

  // tableSourceChartType: remembers which chart type was active before switching to table,
  // so the table view knows which columns/rows to show and can offer "convert back".
  const [tableSourceChartType, setTableSourceChartType] = useState<string>("pie_drilldown");

  // originalSourceChartTypeRef: the chartType prop as it arrived on first render (or last prop change).
  // Used for "Go to source chart" — always goes back to the original, not just one step.
  const originalSourceChartTypeRef = useRef<string>(tableSourceType ?? chartType);

  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const fillHeightWrapperRef = useRef<HTMLDivElement>(null);

  // Keep fillHeight charts responsive: trigger Highcharts reflow whenever the
  // wrapper div changes size (e.g. browser window resize or grid column resize).
  useEffect(() => {
    if (!fillHeight || activeChartType === "table") return;
    const reflow = () => chartRef.current?.chart?.reflow();
    const timer = setTimeout(reflow, 0);
    const el = fillHeightWrapperRef.current;
    if (!el) return () => clearTimeout(timer);
    const ro = new ResizeObserver(reflow);
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [fillHeight, activeChartType]);

  // Sync internal state when the chartType prop changes (e.g. parent remounts the widget).
  useEffect(() => {
    const nextOriginal =
      tableSourceType ??
      (chartType === "table" ? originalSourceChartTypeRef.current : chartType);
    originalSourceChartTypeRef.current = nextOriginal;
    // configuredView, not chartType — this effect re-runs on every prop change
    // and would otherwise immediately undo the saved default view.
    setActiveChartType(configuredView);
    if (chartType !== "table") {
      setTableSourceChartType(chartType);
    } else {
      setTableSourceChartType(nextOriginal);
    }
  }, [chartType, tableSourceType, configuredView]);

  // Build Highcharts options for the current chart type.
  const options = useMemo(() => {
    if (activeChartType === "table") return null;

    const entry = getChartEntry(activeChartType);
    if (!entry) return null;

    // When the user has converted to bar or pie, transform the source data into
    // the expected row format before passing to the renderer.
    // single_bar is only routed through getBarRows when it's a *conversion
    // target* (source chart type is "bar") — single_bar's own native data
    // already carries an optional `percentage` that its renderer falls back
    // to computing from the total when absent; getBarRows/getTopLevelRows
    // would instead default a missing percentage to 0, so native single_bar
    // data must pass straight through unmodified.
    const rendererData =
      activeChartType === "bar"
        ? getBarRows(data, tableSourceChartType)
        : activeChartType === "pie"
          ? getPieRows(data, tableSourceChartType)
          : activeChartType === "single_bar" && tableSourceChartType !== "single_bar"
            ? getBarRows(data, tableSourceChartType)
            : data;

    // onPointClick is only wired up for chart types that support drilldown.
    const pointClickHandler =
      activeChartType === "funnel" || activeChartType === "quadrant_scatter"
        ? onPointClick
        : undefined;

    const baseOptions = entry.renderer(rendererData, spec, title, pointClickHandler);
    if (!baseOptions) return null;

    // isConverted = true when the user is viewing a type different from the source.
    // The context menu uses this to decide whether to show "Go to source chart".
    const isConverted =
      activeChartType !== originalSourceChartTypeRef.current &&
      activeChartType !== tableSourceChartType;

    const { menuItemDefinitions, menuItems } = buildContextMenu(
      activeChartType,
      tableSourceChartType,
      isConverted,
    );

    // Backend-declared download items (has_download_options/download_options)
    // — added alongside whatever conversion/export items buildContextMenu
    // already produced, not instead of them.
    const downloadMenuKeys: string[] = [];
    const downloadMenuDefs: Record<
      string,
      { text: string; onclick: (this: Highcharts.Chart) => void }
    > = {};
    if (downloadOptions?.csv && onDownloadCsvData) {
      // Label follows the format exportDashboardWidgetDataApi is now called
      // with (see ChartWidget.tsx) — this is a backend-generated download,
      // not the client-side "Download CSV" item below it.
      downloadMenuDefs.downloadCsvData = {
        text: "Download Excel Data",
        onclick: onDownloadCsvData,
      };
      downloadMenuKeys.push("downloadCsvData");
    }
    // Gated on the presence of downloadOptions -- callers only pass it when
    // has_download_options is true -- rather than on download_options.case_data.
    // The per-case export is offered wherever the backend advertises downloads
    // at all, consistently across every chart type.
    if (downloadOptions && onDownloadCaseData) {
      downloadMenuDefs.downloadCaseData = {
        text: "Download Case Data",
        onclick: onDownloadCaseData,
      };
      downloadMenuKeys.push("downloadCaseData");
    }

    // Merge the context menu items into the renderer's exporting config.
    // Renderer configs set button position; we layer on top with our menu items.
    return {
      ...baseOptions,
      exporting: {
        ...(baseOptions.exporting ?? {}),
        menuItemDefinitions: {
          ...(baseOptions.exporting?.menuItemDefinitions ?? {}),
          ...(menuItemDefinitions as any),
          // With a description available, CSV downloads carry the widget
          // metadata; without one, Highcharts' built-in downloadCSV runs.
          ...(description
            ? {
                downloadCSV: {
                  text: "Download CSV",
                  onclick: function (this: Highcharts.Chart) {
                    downloadCsvWithMeta(this, title, description);
                  },
                },
              }
            : {}),
          ...downloadMenuDefs,
        },
        buttons: {
          ...(baseOptions.exporting?.buttons ?? {}),
          contextButton: {
            ...(baseOptions.exporting?.buttons?.contextButton ?? {}),
            menuItems: [...menuItems, ...downloadMenuKeys],
          },
        },
      },
    };
  }, [
    activeChartType,
    tableSourceChartType,
    data,
    spec,
    title,
    description,
    onPointClick,
    downloadOptions,
    onDownloadCsvData,
    onDownloadCaseData,
  ]);

  // Redraw after options change so Highcharts picks up series/axis updates.
  useEffect(() => {
    if (activeChartType !== "table" && chartRef.current && options) {
      chartRef.current.chart.redraw();
    }
  }, [options, activeChartType]);

  // Table view is a completely different render path — no Highcharts chart,
  // just a styled <table> with an invisible Highcharts shell for the menu button.
  if (activeChartType === "table") {
    return (
      <ChartTableView
        chartId={chartId}
        title={title}
        description={description}
        sourceChartType={tableSourceChartType}
        data={data}
        onSetChartType={(nextType) => setActiveChartType(nextType)}
      />
    );
  }

  if (!options) return null;

  const entry = getChartEntry(activeChartType);

  // fillHeight: let the chart expand to fill its CSS container.
  // Setting height: null tells Highcharts to use 100% of the wrapper div.
  const chartOptions = fillHeight
    ? { ...options, chart: { ...(options.chart ?? {}), height: null } }
    : options;

  const chartNode = (
    <HighchartsReact
      key={activeChartType}
      containerProps={{
        id: chartId,
        ...(fillHeight ? { style: { height: "100%", width: "100%" } } : {}),
      }}
      ref={chartRef}
      highcharts={Highcharts}
      options={chartOptions}
      callback={(chart: Highcharts.Chart) => {
        // Attach conversion methods to the chart instance so Highcharts context-menu
        // onclick handlers (which receive `this = chart`) can trigger React state changes.
        const tc = chart as ToggleableChart;
        tc.setChartType = (nextChartType: string) => {
          setActiveChartType((currentChartType: string) => {
            if (nextChartType === "table" && currentChartType !== "table") {
              setTableSourceChartType(originalSourceChartTypeRef.current);
            }
            return nextChartType;
          });
        };
        tc.convertToSourceChart = () => {
          setActiveChartType(originalSourceChartTypeRef.current);
        };
      }}
      // Map charts require a different Highcharts constructor and the GeoJSON data.
      {...(entry?.constructorType === "mapChart"
        ? { constructorType: "mapChart", mapData, id: "mapChart" }
        : {})}
    />
  );

  if (fillHeight) {
    return (
      <div ref={fillHeightWrapperRef} style={{ width: "100%", height: "100%", minHeight: 0 }}>
        {chartNode}
      </div>
    );
  }

  return chartNode;
}
